
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import TranslationsRepository, { TermWithTranslation } from "../../repositories/implementations/TranslationsRepository";
import { Project } from "../../entities/Project";
import { ProjectLanguage } from "../../entities/ProjectLanguage";
import { getLanguageLabel } from "../../utils/languageUtils";
import LanguageDropdown from "../../components/LanguageDropdown.vue";
import { toastError } from "../../utils/toast";

@Component({ components: { LanguageDropdown } })
export default class TranslationsByLanguageView extends Vue {
  @Prop({ required: true }) project!: Project;

  @Prop({ required: true }) languages!: ProjectLanguage[];

  activeLanguageCode = "";

  viewFilter: "all" | "missing" = "all";

  search = "";

  pageSize = 10;

  currentPage = 1;

  totalCount = 0;

  pageSizeOptions = [10, 50, 100];

  rows: TermWithTranslation[] = [];

  loading = false;

  savingKey = "";

  invalidLanguage = false;

  searchTimer: ReturnType<typeof setTimeout> | null = null;

  // Pending plural partial edits: termId → { one, other }
  pluralPending: Record<string, { one: string | null; other: string | null }> = {};

  translationsRepo = new TranslationsRepository();

  get filterOptions() {
    return [
      { text: "Show all", value: "all" },
      { text: "Show missing only", value: "missing" },
    ];
  }

  get languageItems() {
    return this.languages.map((l) => ({
      text: getLanguageLabel(l.languageCode),
      value: l.languageCode,
    }));
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  isMissing(row: TermWithTranslation): boolean {
    if (!row.translation) return true;
    return !row.translation.isComplete(row.term.isPlural);
  }

  async created() {
    const { code } = this.$route.params;
    const valid = this.languages.some((l) => l.languageCode === code);
    if (!valid) {
      this.invalidLanguage = true;
      return;
    }
    this.activeLanguageCode = code;
    await this.loadRows();
  }

  @Watch("$route.params.code")
  async onCodeChanged(newCode: string) {
    const valid = this.languages.some((l) => l.languageCode === newCode);
    if (!valid) {
      this.invalidLanguage = true;
      return;
    }
    this.invalidLanguage = false;
    this.activeLanguageCode = newCode;
    this.pluralPending = {};
    this.currentPage = 1;
    await this.loadRows();
  }

  @Watch("search")
  onSearchChanged() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(async () => {
      this.currentPage = 1;
      await this.loadRows();
    }, 300);
  }

  @Watch("viewFilter")
  async onViewFilterChanged() {
    this.currentPage = 1;
    await this.loadRows();
  }

  @Watch("pageSize")
  async onPageSizeChanged() {
    this.currentPage = 1;
    await this.loadRows();
  }

  onLanguageChange(code: string) {
    this.$router.push({
      name: "project-translations-by-language",
      params: { projectId: this.project.id, code },
    });
  }

  async prevPage() {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      await this.loadRows();
    }
  }

  async nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      await this.loadRows();
    }
  }

  async loadRows() {
    this.loading = true;
    try {
      const { rows, total } = await this.translationsRepo.getForLanguageEditorPaginated(
        this.project.id,
        this.activeLanguageCode,
        this.currentPage,
        this.pageSize,
        { search: this.search || undefined, missingOnly: this.viewFilter === "missing" }
      );
      this.rows = rows;
      this.totalCount = total;
    } catch (e: any) {
      toastError("Failed to load translations.");
    } finally {
      this.loading = false;
    }
  }

  async saveTranslation(row: TermWithTranslation, valueOne: string, valueOther: string | null) {
    const trimmed = valueOne.trim();
    if (!trimmed) return;
    const existing = row.translation;
    if (existing && trimmed === existing.valueOne?.trim() && valueOther === existing.valueOther) return;
    this.savingKey = row.term.id;
    try {
      const saved = await this.translationsRepo.upsert(row.term.id, this.activeLanguageCode, trimmed, valueOther);
      const idx = this.rows.findIndex((r) => r.term.id === row.term.id);
      if (idx !== -1) {
        this.$set(this.rows, idx, { term: row.term, translation: saved });
      }
      this.$emit("translations-updated");
    } catch (e: any) {
      toastError("Save failed.");
    } finally {
      if (this.savingKey === row.term.id) this.savingKey = "";
    }
  }

  async savePluralOne(row: TermWithTranslation, valueOne: string) {
    const key = row.term.id;
    if (!this.pluralPending[key]) this.pluralPending[key] = { one: null, other: null };
    this.pluralPending[key].one = valueOne.trim();

    const { one, other } = this.pluralPending[key];
    const existingOther = row.translation?.valueOther ?? null;
    const resolvedOther = other ?? existingOther;

    if (one && resolvedOther !== null) {
      await this.savePlural(row, one, resolvedOther);
    }
  }

  async savePluralOther(row: TermWithTranslation, valueOther: string) {
    const key = row.term.id;
    if (!this.pluralPending[key]) this.pluralPending[key] = { one: null, other: null };
    this.pluralPending[key].other = valueOther.trim();

    const { one, other } = this.pluralPending[key];
    const existingOne = row.translation?.valueOne ?? null;
    const resolvedOne = one ?? existingOne;

    if (resolvedOne && other !== null) {
      await this.savePlural(row, resolvedOne, other);
    }
  }

  async savePlural(row: TermWithTranslation, one: string, other: string) {
    this.savingKey = `${row.term.id}:one`;
    try {
      const saved = await this.translationsRepo.upsert(row.term.id, this.activeLanguageCode, one, other);
      const idx = this.rows.findIndex((r) => r.term.id === row.term.id);
      if (idx !== -1) {
        this.$set(this.rows, idx, { term: row.term, translation: saved });
      }
      delete this.pluralPending[row.term.id];
      this.$emit("translations-updated");
    } catch (e: any) {
      toastError("Save failed.");
    } finally {
      this.savingKey = "";
    }
  }
}
